// Shared helpers for the Promotions feature (new promotions-engine contract).

// ---- Status ↔ Tab mapping ---------------------------------------------------
export const PROMO_STATUS = {
    SUGGESTED: "SUGGESTED",
    SCHEDULED: "SCHEDULED",
    ACTIVE: "ACTIVE",
    ENDED: "ENDED",
    DISCARDED: "DISCARDED",
};

export const PROMO_TABS = [
    { id: "active", label: "Running Now", status: PROMO_STATUS.ACTIVE },
    { id: "ai", label: "Alfred AI Suggested", status: PROMO_STATUS.SUGGESTED },
    { id: "scheduled", label: "Scheduled", status: PROMO_STATUS.SCHEDULED },
    { id: "past", label: "Past", status: PROMO_STATUS.ENDED },
];

// ---- Target audience --------------------------------------------------------
export const AUDIENCE_OPTIONS = [
    { value: "PRE_CHECKIN", label: "Pre-Arrival" },
    { value: "CHECKED_IN", label: "In-House" },
    { value: "PRE_CHECKIN_CHECKED_IN", label: "All Guests" },
    { value: "ALFRED", label: "Let Alfred Decide" },
];

export const audienceLabel = (value) => {
    const found = AUDIENCE_OPTIONS.find((a) => a.value === value);
    return found ? found.label : value || "N/A";
};

// ---- Offerable services (mirrors backend types.OfferableServiceValues) -------
export const OFFERABLE_SERVICES = [
    "Spa",
    "In Room Dining",
    "Restaurant Dining",
    "PreCheckin Cab Request",
    "PostCheckin Cab Request",
    "PreCheckin Early Checkin Request",
    "PostCheckin ExtendStay Request",
    "PostCheckin Late Checkout Request",
];

// ---- Currency (INR) ---------------------------------------------------------
// Ledger figures: always two decimals. GST lands on half-rupees constantly (5% of 350 is
// 17.50), and rounding each row to whole rupees made columns that did not add up to their
// own subtotal, which is computed from the raw values and rounded once.
export const formatINR = (amount) => {
    const value = Number(amount) || 0;
    return `₹${value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

// Headline and label figures: a promo is authored as a round number and a KPI tile is read
// at a glance, so neither wants trailing paise.
export const formatINRWhole = (amount) => {
    const value = Number(amount) || 0;
    return `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

// Describe an offer, e.g. "20% OFF" / "₹500 OFF"
export const offerLabel = (promo) => {
    if (!promo || !promo.offer_type || !promo.offer_value) return "";
    if (promo.offer_type === "PERCENT_OFF") return `${promo.offer_value}% OFF`;
    if (promo.offer_type === "AMOUNT_OFF") return `${formatINRWhole(promo.offer_value)} OFF`;
    return "";
};

// ---- Send / target window ---------------------------------------------------
const SEND_PHASE_LABELS = {
    PRE_ARRIVAL: "Pre-Arrival",
    IN_STAY: "In-Stay",
    PRE_DEPARTURE: "Pre-Departure",
    BOTH: "Anytime during stay",
};

const LIFECYCLE_TRIGGER_LABELS = {
    PRE_ARRIVAL_T3D: "3 days before arrival",
    PRE_ARRIVAL_T2D: "2 days before arrival",
    PRE_ARRIVAL_T1D: "1 day before arrival",
    ARRIVAL_DAY: "On arrival day",
    CHECKIN_PLUS_1H: "1 hour after check-in",
    FIRST_EVENING: "First evening",
    NEXT_MORNING: "Next morning",
    MID_STAY: "Mid-stay",
    DAY_BEFORE_CHECKOUT: "Day before checkout",
    CHECKOUT_MORNING: "Checkout morning",
};

export const sendPhaseLabel = (phase) => SEND_PHASE_LABELS[phase] || phase || "";
export const lifecycleTriggerLabel = (trigger) => LIFECYCLE_TRIGGER_LABELS[trigger] || trigger || "";

// Daily time window, e.g. "07:00 – 09:00"
export const dailyWindowLabel = (promo) => {
    if (!promo?.time_window_start && !promo?.time_window_end) return "";
    return `${promo.time_window_start || "—"} – ${promo.time_window_end || "—"}`;
};

// ---- Dates ------------------------------------------------------------------
export const formatPrettyDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};
